/**
 * Lazy content catalogs (integration batch 4, written-budget refactor).
 * Species and morph JSON ship as dynamic-import chunks fetched after the
 * first scene; the sim worker awaits loadCatalogs() at init, so the sim
 * blocks only until the catalogs are ready. Content-Data keeps authoring
 * data/species/*.json and data/morphs/*.json as-is - the glob picks new
 * files up automatically, no import list to maintain.
 */
const speciesGlob = import.meta.glob('../../data/species/*.json');
const morphGlob = import.meta.glob('../../data/morphs/*.json');

export interface CatalogCache {
  species: unknown[];
  morphs: unknown[];
}

let cache: CatalogCache | null = null;
let inflight: Promise<CatalogCache> | null = null;

async function loadAll(glob: Record<string, () => Promise<unknown>>): Promise<unknown[]> {
  const keys = Object.keys(glob).sort(); // deterministic assembly order
  const mods = await Promise.all(keys.map((k) => glob[k]()));
  return mods.map((m) => (m as { default: unknown }).default);
}

/** Fetch every catalog chunk once; safe to call concurrently. */
export function loadCatalogs(): Promise<CatalogCache> {
  if (cache !== null) return Promise.resolve(cache);
  inflight ??= Promise.all([loadAll(speciesGlob), loadAll(morphGlob)]).then(([species, morphs]) => {
    cache = { species, morphs };
    return cache;
  });
  return inflight;
}

/** Sync access after loadCatalogs() resolves; throws before. */
export function catalogs(): CatalogCache {
  if (cache === null) throw new Error('catalogs not loaded - await loadCatalogs() first');
  return cache;
}
